import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { departmentsApi } from "../../api/departmentsApi";
import { businessUnitsApi } from "../../api/businessUnitsApi";
import type { Department } from "../../types/department.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { withRecordMedia } from "../../utils/recordMedia";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface DepartmentsState {
  items: Department[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: DepartmentsState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setDepartments: (state, action: PayloadAction<Department[]>) => {
      state.items = action.payload;
    },
    appendDepartments: (state, action: PayloadAction<Department[]>) => {
      state.items = [...state.items, ...action.payload];
    },

    addSingleDepartmentLocally: (state, action: PayloadAction<Department>) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetDepartmentList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },

    updateSingleDepartment: (
      state,
      action: PayloadAction<Partial<Department> & { id: string }>,
    ) => {
      const index = state.items.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },

    removeSingleDepartment: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((d) => d.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setDepartments,
  appendDepartments,
  addSingleDepartmentLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetDepartmentList,
  updateSingleDepartment,
  removeSingleDepartment,
} = departmentsSlice.actions;

interface FetchDepartmentsPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  businessUnitIds?: string[];
  ids?: string[];
  isActive?: boolean | null;
  codes?: string[];
  searchText?: string;
  includeDeleted?: boolean;
  /** Sign photo links (default: only for page-sized or by-id fetches). */
  withMedia?: boolean;
}

// The newest first-page request (see fetchDepartments).
let latestDepartmentsPage = 0;

export const fetchDepartments = (payload: FetchDepartmentsPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().departments;
    const userData = await getCurrentUser();

    // `??` (not `||`) so an explicit pageIndex of 0 isn't treated as absent.
    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    // A first page always runs (a new search must not be dropped because an
    // earlier one is still loading); an answer to an older one is ignored.
    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) return;
    const ticket = isFirstPage
      ? ++latestDepartmentsPage
      : latestDepartmentsPage;
    const superseded = () => ticket !== latestDepartmentsPage;

    try {
      dispatch(setLoading(true));

      // Department documents in the search index aren't tagged with an
      // organisation id directly (only businessUnitId.keyword is indexed),
      // so filtering by groupIds always returns zero matches. Departments
      // are scoped through their Business Unit instead: resolve the current
      // organisation's business unit ids (businessUnitsApi is already
      // correctly org-scoped) and filter on those, unless the caller already
      // narrowed to specific business units via the filter UI.
      let businessUnitIds: string[] = payload.businessUnitIds || [];
      if (businessUnitIds.length === 0) {
        businessUnitIds = await businessUnitsApi.getOrganisationUnitIds(
          `${userData.companyId}`,
        );
      }

      // If the organisation genuinely has no business units, there can be no
      // departments — return empty rather than falling through to an
      // unfiltered (cross-organisation) search.
      if (superseded()) return;
      if (businessUnitIds.length === 0) {
        if (superseded()) return;
        dispatch(setTotalCount(0));
        dispatch(setHasMore(false));
        if (incomingPageIndex === 0) {
          dispatch(setDepartments([]));
          dispatch(setPageIndex(1));
        }
        return;
      }

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // "Sort by" from the listing (resolveSort gives sortAscending; this
        // endpoint takes the opposite flag).
        sortDecending: !(payload.sortAscending ?? false),
        businessUnitIds,
        ids: payload.ids,
        isActive: payload.isActive ?? null,
        codes: payload.codes || [],
        searchText: payload.searchText || "",
        // A lookup by id (a detail page, often opened fresh from the URL)
        // must find the record whatever its state: without this a deleted
        // record's page loaded once from the list and then came back empty
        // on reload, because the search defaults to excluding deleted rows.
        includeDeleted: payload.includeDeleted ?? !!payload.ids?.length,
      };

      const response = await departmentsApi.searchDepartments(requestBody);
      // Pictures and files are stored as ids; resolve their links for the
      // whole page in one request (utils/recordMedia).
      // Photo links are signed only for a page someone looks at (a listing
      // page or a lookup by id). A 100-500 row fetch that fills a filter
      // dropdown shows no photos, and signing them cost a second request.
      const withMedia =
        payload.withMedia ??
        ((payload.pageSize ?? 10) <= 50 || !!payload.ids?.length);
      const fetchedData = withMedia
        ? await withRecordMedia<any>(response.data || [])
        : response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      if (superseded()) return;
      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setDepartments(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendDepartments(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Departments error:", error);
      toast.error(
        i18n.t("departments:department.messages.fetchError", {
          defaultValue: "Failed to fetch departments",
        }),
      );
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const addDepartment = (data: Partial<Department>) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));
      const res = await departmentsApi.createDepartment(data);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newDepartment: Department = {
        ...(data as Department),
        id: typeof createdId === "string" ? createdId : createdId?.id,
        isActive: true,
        creationDate: new Date().toISOString(),
      };

      dispatch(addSingleDepartmentLocally(newDepartment));

      toast.success(
        i18n.t("departments:department.messages.addSuccess", {
          defaultValue: "Department created successfully",
        }),
      );
      return newDepartment;
    } catch (error) {
      console.error("Create Department error:", error);
      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("departments:department.messages.addError", {
            defaultValue: "Failed to create department",
          }),
        ),
      );
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};
export const editDepartment = (data: Partial<Department> & { id: string }) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setEditing(true));
      // An edit replaces the whole record, and the edit forms know nothing
      // of the picture or the files — carry them over unless the caller is
      // the one changing them.
      const existing: any = getState().departments.items.find(
        (item: any) => item.id === data.id,
      );
      const payload = {
        profilePic: existing?.profilePic,
        mediaJson: existing?.mediaJson,
        ...data,
      };
      const res = await departmentsApi.updateDepartment(payload);
      const updatedDepartment = res?.data || payload;

      dispatch(updateSingleDepartment(updatedDepartment));

      toast.success(
        i18n.t("departments:department.messages.editSuccess", {
          defaultValue: "Department updated successfully",
        }),
      );
      return updatedDepartment;
    } catch (error) {
      console.error("Edit Department error:", error);
      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("departments:department.messages.editError", {
            defaultValue: "Failed to update department",
          }),
        ),
      );
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const activateDepartment = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(updateSingleDepartment({ id, isActive: true }));

      await departmentsApi.activateDepartment(id);

      toast.success(
        i18n.t("departments:department.messages.activateSuccess", {
          defaultValue: "Department activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleDepartment({ id, isActive: false }));
      console.error("Activate Department error:", error);
      toast.error(
        i18n.t("departments:department.messages.activateError", {
          defaultValue: "Failed to activate department",
        }),
      );
    }
  };
};

export const deactivateDepartment = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(updateSingleDepartment({ id, isActive: false }));

      await departmentsApi.deActivateDepartment(id);

      toast.success(
        i18n.t("departments:department.messages.deactivateSuccess", {
          defaultValue: "Department deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleDepartment({ id, isActive: true }));
      console.error("Deactivate Department error:", error);
      toast.error(
        i18n.t("departments:department.messages.deactivateError", {
          defaultValue: "Failed to deactivate department",
        }),
      );
    }
  };
};

export const removeDepartment = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleDepartment(id));

      await departmentsApi.removeDepartment(id);

      toast.success(
        i18n.t("departments:department.messages.removeSuccess", {
          defaultValue: "Department removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Department error:", error);
      // The API refuses deletion while contracts or staff still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("departments:department.messages.removeError", {
        defaultValue: "Failed to remove department",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default departmentsSlice.reducer;
